import { ArrowsAltOutlined, DashboardOutlined, EnvironmentOutlined, FilePdfOutlined, HomeOutlined, InfoCircleOutlined, MailOutlined, MergeCellsOutlined, PhoneOutlined, RadiusBottomrightOutlined, ReconciliationOutlined, WhatsAppOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  FloatButton,
  Menu,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
} from "antd";
import Modal from "antd/lib/modal/Modal";
import { ColumnsType } from "antd/lib/table";
import { MenuInfo } from "rc-menu/lib/interface";
import { useContext, useEffect, useState } from "react";
import { Form, useNavigate } from "react-router-dom";
import { SignedInContext } from "../AppEnv";
import { SignedInDoctor } from "../auth/interfaces";
import { COMPARE_EXAMS_PATH, PATIENTS_PATH, PDF_EXAMS_PATH } from "../env";
import DeleteExamModal from "../exams/DeleteExamModal";
import {
  buildExamTypeFilters,
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_SLUGNAME,
  extractExamTypeInfo,
} from "../exams/exams_definitions_utils";
import {
  invalidPosturalExamination,
  PosturalExamination,
  PosturalExaminationSelection,
  PosturalTestDefinitionsMap,
} from "../exams/interfaces";
import {
  getAllPosturalExaminationForPatient,
  getPosturalExamDefinition,
  getPosturalExaminationData,
} from "../exams/utils";
import { GenderType, Patient } from "./interfaces";
import TutorialButtonYouTube from "../exams/TutorialButtonYouTube";

interface PosturalExaminationRow extends PosturalExamination {
  key: number;
}

const examTypes = [
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_SLUGNAME,
];

const ActionsMenu = ({
  patientId,
  examId,
  navigate,
  openDelExamModal,
}: {
  patientId: number;
  examId: number;
  navigate: Function;
  openDelExamModal: Function;
}) => (
  <Menu
    onClick={(clickedValue: MenuInfo) => {
      switch (clickedValue.key) {
        case "delExam":
          openDelExamModal(examId);
          break;
        case "pdfPreview":
          navigate("/app" + PDF_EXAMS_PATH + "/" + patientId + "/" + examId);
          break;
       
        default:
          // Codice per gestire i casi non previsti
          break;
      }
    }}
    items={[
      {
        key: "pdfPreview",
        label: "Genera PDF",
      },
       
      {
        key: "delExam",
        label: "Elimina esame",
      },
    ]}
  />
);

const ToggleButton = ({
  examId,
  handleExamSelection,
}: {
  examId: number;
  handleExamSelection: Function;
}) => {
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    handleExamSelection(examId, checked);
  }, [checked]);

  return (
    <Button
      onClick={(e) => setChecked(!checked)}
      style={
        checked
          ? { color: "white", background: "#9edcc4" }
          : { color: "black", background: "#9edcc44d" }
      } 
    >
      {examId}
    </Button>
  );
};

const PatientPosturalExamsTable = ({
  patient,
  setExamDelSuccess,
}: {
  patient: Patient;
  setExamDelSuccess: Function;
}) => {
  const navigate = useNavigate();

  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;

  //add by Leo for resize table
  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    //mount
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    //unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [posturalExaminations, setPosturalExaminations] = useState<
    PosturalExaminationRow[]
  >([]);

  const [statusModalInfoVisible, setStatusModalInfoVisible] =
    useState<boolean>(false);

  const [
    posturalExaminationTestsDefinition,
    setPosturalExaminationTestsDefinition,
  ] = useState<PosturalTestDefinitionsMap>({});

  // Delete postural examination
  const [delPosturalExamination, setDelPosturalExamination] =
    useState<PosturalExamination>(invalidPosturalExamination);
  const [delPosturalExaminationVisible, setDelPosturalExaminationVisible] =
    useState<boolean>(false);

  const openDelExamModal: Function = (examId: number) => {
    getPosturalExaminationData(
      signedInDoctor,
      patient.id || -1,
      examId,
      false
    ).then((exam: PosturalExamination) => {
      setDelPosturalExamination(exam);
    });
  };

  // load postural test definitions and test selections
  useEffect(() => {
    getPosturalExamDefinition(signedInDoctor).then((testDefinitions) => {
      // load test definitions
      let testDefinitionsMap: PosturalTestDefinitionsMap = {};
      if (testDefinitions != undefined)
        for (let examType of examTypes)
          testDefinitionsMap[examType] = testDefinitions.filter((testDef) =>
            testDef.posturalTestSlugName.startsWith(examType)
          );
      setPosturalExaminationTestsDefinition(testDefinitionsMap);

      getAllPosturalExaminationForPatient(
        signedInDoctor,
        patient.id || -1,
        true
      ).then((examinations: PosturalExamination[]) => {
        for (let pe of examinations)
          if (pe.testSelections && testDefinitionsMap[pe.type] != undefined) {
            //insert test passed value

            for (let ts of pe.testSelections) {
              const examPassedOptions = testDefinitionsMap[pe.type]
                .filter((td) => td.id == ts.testId)[0]
                .posturalTestOptions.filter((to) => to.examPassedOption);
              ts.passedValue =
                examPassedOptions.length > 0
                  ? examPassedOptions[0].posturalOptionId
                  : -2;
            }
          }
        setPosturalExaminations(
          examinations.map((ex) => ({ ...ex, key: ex.examId }))
        );
      });
    });
  }, [patient]);

  //selected delExam, open modal for it
  useEffect(() => {
    if (
      delPosturalExamination.patientId == patient.id &&
      delPosturalExamination.examId > 0
    )
      setDelPosturalExaminationVisible(true); //Del exam has been selected
  }, [delPosturalExamination]);

  //selected delExam, modal has been closed: check if exam still exists, otherwise delete it
  useEffect(() => {
    if (delPosturalExamination.examId > 0 && !delPosturalExaminationVisible) {
      getPosturalExaminationData(
        signedInDoctor,
        patient.id || -1,
        delPosturalExamination.examId,
        false
      )
        .then((posturalExam) => {
          if (posturalExam.examId != delPosturalExamination.examId) {
            setDelPosturalExamination(invalidPosturalExamination);
            setPosturalExaminations(
              (posturalExaminations: PosturalExaminationRow[]) =>
                posturalExaminations.filter(
                  (pe: PosturalExaminationRow) =>
                    pe.examId != delPosturalExamination.examId
                )
            );
          }
        })
        .catch((err) => console.log(err));
    }
  }, [delPosturalExaminationVisible]);

  ///LEO

  const numberOfExams = posturalExaminations.length;

  // console.log("PT" + numberOfExams);

  const getAllExamIds = () => {
    return posturalExaminations.map((exam) => exam.examId);
  };

  const [filteredDate, setFilteredDate] = useState<any>(null);

  const examIdsArray = getAllExamIds();
  // console.log(examIdsArray);

  //LEOB

  const [compareVisible, setCompareVisible] = useState<boolean>(false);
  const [genPdfVisible, setGenPdfVisible] = useState<boolean>(false);
  const [selectedExams, setSelectedExams] = useState<number[]>([]);
  const [agePatient, setAgePatient] = useState<number>(0);
  const [selectedNumExams, setSelectedNumExams] = useState(0);
  const [genderPatient, setGenderPatient] = useState<string>("");


  useEffect(() => {
    if (patient.birthDate) {
      const birthDate = new Date(patient.birthDate);
      const ageDifMs = Date.now() - birthDate.getTime();
      const ageDate = new Date(ageDifMs);
      const age = Math.abs(ageDate.getUTCFullYear() - 1970);
      setAgePatient(age);
    }
  }, [patient.birthDate]);

  useEffect(() => {
    return setGenderPatient(patient.gender === 0 ? "Maschio" : "Femmina");
  }, [patient.gender]);


  const handleExamSelection = (examId: number, checked: boolean) => {
    if (checked) {
      setSelectedExams((prevSelectedExams) => [...prevSelectedExams, examId]);
    } else {
      setSelectedExams((prevSelectedExams) =>
        prevSelectedExams.filter((id) => id !== examId)
      );
    }
  };

  

  useEffect(() => {
    //console.log("selectedExams ", selectedExams);
    setGenPdfVisible(selectedExams.length >= 1);
    //console.log("num of exam" + selectedExams.length);
  }, [selectedExams]);

  

  useEffect(() => {
    //console.log("selectedExams ", selectedExams);
    const selectedExamTypes = selectedExams.map(
      (examId) => posturalExaminations.find((exam) => exam.examId === examId)?.type
    );
    const hasSameTypeExams = selectedExamTypes.some(
      (type, index) => selectedExamTypes.indexOf(type) !== index
    );
    setCompareVisible(hasSameTypeExams && selectedExams.length <= 2);
    //console.log("num of exam" + selectedExams.length);
  }, [selectedExams, posturalExaminations]);

  useEffect(() => {
    setSelectedNumExams(selectedExams.length);
  }, [selectedExams]);

  const columns: ColumnsType<PosturalExaminationRow> = [
    {
      title: "Id",
      dataIndex: "key",
      sorter: {
        compare: (a, b) => a.key - b.key,
        multiple: 1, // Consentire solo l'ordinamento ascendente e discendente
      },
      defaultSortOrder: "descend", // Imposta l'ordinamento discendente di default
      render: (_, record) => (
        <ToggleButton
          examId={record.examId}
          handleExamSelection={(examId: number, selected: boolean) =>
            handleExamSelection(examId, selected)
          }
        />
      ),
    },
    {
      title: "Data",
      dataIndex: "date",
      key: "date",
      render: (text) => (
        <>
          {text !== undefined ? new Date("" + text).toLocaleDateString() : "-"}
        </>
      ),
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <div className="borderolllo" style={{ padding: 8 }}>
          <DatePicker
            onChange={(date, dateString) => setFilteredDate(dateString)}
            style={{ marginBottom: 8, display: "block" }}
          />

          <Button
            className="centeros"
            onClick={() => {
              const keys = filteredDate ? [filteredDate] : [];
              setSelectedKeys(keys);
              confirm();
            }}
          >
            Filtra
          </Button>
        </div>
      ),
      onFilter: (value, record) => {
        const recordDate = record.date
          ? new Date(record.date).toISOString().slice(0, 10)
          : null;

        if (!filteredDate) {
          // Se il filtro è vuoto, visualizza tutti i dati
          return true;
        }

        return recordDate === filteredDate;
      },
    },
    {
      title: "Esame",
      dataIndex: "type",
      key: "type",
      filters: buildExamTypeFilters(),
      render: (type: string) => (
        <Tag color={extractExamTypeInfo(type).color}>{type.toUpperCase()}</Tag>
      ),
      onFilter: (value: any, record) => value && record.type == value,
    },
    {
      title: (
        <>
          {"Stato "}
          <InfoCircleOutlined
            onClick={() => setStatusModalInfoVisible(true)}
            rev={undefined}
          />
        </>
      ),
      dataIndex: "testSelections",
      key: "testSelections",
      render: (testSelections: PosturalExaminationSelection[]) => {
        const numTestMade = testSelections
          ? testSelections.filter(
              (pe) => pe.selectedValue >= 0 && pe.passedValue != -2
            ).length
          : 0;
        const numTestPassed = testSelections
          ? testSelections.filter((pe) => pe.selectedValue == pe.passedValue)
              .length
          : 0;
        const numTestSelectionsNotCounted = testSelections
          ? testSelections.filter((pe) => pe.passedValue == -2).length
          : 0;
        const numTestMissed = numTestMade > 0 ? numTestMade - numTestPassed : 0;
        const pendingTests = testSelections
          ? testSelections.length - numTestMade - numTestSelectionsNotCounted
          : 0;
        return (
          <>
            <Tag color={"red"}>{testSelections ? numTestMissed : "-"}</Tag>
            <Tag color={"green"}>{testSelections ? numTestPassed : "-"}</Tag>
            <Tag color={"yellow"}>
              {testSelections && pendingTests > 0 ? pendingTests : "-"}
            </Tag>
          </>
        );
      },
    },
    {
      title: "Azione",
      key: "action",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <ActionsMenu
              patientId={record.patientId}
              examId={record.examId}
              navigate={navigate}
              openDelExamModal={openDelExamModal}
            />
          }
          style={{ color: "white" }}
          onClick={() =>
            navigate(
              "/app" +
                PATIENTS_PATH +
                "/" +
                record.patientId +
                "/" +
                record.examId
            )
          }
        >
          Apri esame
        </Dropdown.Button>
      ),
    },
  ];

  //Add BY LEO for resize table (colomun fromat)
  const columns2: ColumnsType<PosturalExaminationRow> = [
    {
      title: "Id",
      dataIndex: "key",
      sorter: {
        compare: (a, b) => a.key - b.key,
        multiple: 1, // Consentire solo l'ordinamento ascendente e discendente
      },
      defaultSortOrder: "descend", // Imposta l'ordinamento discendente di default
      render: (_, record) => (
        <ToggleButton
          examId={record.examId}
          handleExamSelection={(examId: number, selected: boolean) =>
            handleExamSelection(examId, selected)
          }
        />
      ),
    },

    {
      title: "Esame",
      dataIndex: "type",
      key: "type",
      filters: buildExamTypeFilters(),
      render: (type: string) => (
        <Tag color={extractExamTypeInfo(type).color}>{type.toUpperCase()}</Tag>
      ),
      onFilter: (value: any, record) => value && record.type == value,
    },

    {
      title: "Azione",
      key: "action",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <ActionsMenu
              patientId={record.patientId}
              examId={record.examId}
              navigate={navigate}
              openDelExamModal={openDelExamModal}
            />
          }
          onClick={() =>
            navigate(
              "/app" +
                PATIENTS_PATH +
                "/" +
                record.patientId +
                "/" +
                record.examId
            )
          }
        >
          Apri
        </Dropdown.Button>
      ),
    },
  ];

  return (
    <>
      {width >= 640 && (
        <div>
          <TutorialButtonYouTube
            video={
              "https://www.youtube.com/embed/ZigSLtrDehI?si=Oh2xuJl1tqZkx-Zu"
            }
            title={"Tutorial PhysiocheckApp | Generazione PDF Multipli"}
          />

          {genPdfVisible && (
            <>
              <FloatButton
                tooltip={<div>Genera PDF</div>}
                badge={{ count: selectedNumExams }}
                icon={<FilePdfOutlined rev={undefined} />}
                type="default"
                style={{ right: 24 }}
                onClick={() => {
                  navigate(
                    "/app" +
                      PDF_EXAMS_PATH +
                      "/" +
                      patient.id +
                      "/multiple_exams/" +
                      selectedExams.join("-")
                  );
                }}
              />
            </>
          )}


          {compareVisible && (
            <>
              <FloatButton
                tooltip={<div>Compara Esami</div>}
                badge={{ count: selectedNumExams }}
                icon={ <MergeCellsOutlined rev={undefined} /> }
                type="default"
                style={{ right: 24, bottom: 100 }}
                onClick={() => {
                  navigate(
                    "/app" +
                      COMPARE_EXAMS_PATH +
                      "/" +
                      patient.id +
                      "/multiple_exams/" +
                      selectedExams.join("-")
                  );
                }}
              />
            </>
          )}

          <div className="sss"></div>
          <Row style={{ backgroundColor: "rgba(158, 220, 196, 0.35)", padding: 15, margin: 10, borderRadius: 25 }}>
         
          <Col span={24}>
          <h2 style={{  color: "grey" }}> {patient.name} {patient.surname}  </h2>
          </Col>
          
    <Col span={8}>
    
    <div>
  <h3 style={{ display: 'inline', color: "grey" }}> <HomeOutlined rev={undefined} /> Sesso: </h3>
  <h5 style={{ display: 'inline', color: "grey" }}> {genderPatient} </h5>
</div>

    <div>
  <h3 style={{ display: 'inline', color: "grey" }}> <MailOutlined rev={undefined} /> Età: </h3>
  <h5 style={{ display: 'inline', color: "grey" }}> {agePatient} </h5>
</div>

<div>
  <h3 style={{ display: 'inline', color: "grey" }}> <DashboardOutlined  rev={undefined} /> Peso: </h3>
  <h5 style={{ display: 'inline', color: "grey" }}> kg {patient.weight} </h5>
</div>

<div>
  <h3 style={{ display: 'inline', color: "grey" }}> <ArrowsAltOutlined   rev={undefined} /> Altezza: </h3>
  <h5 style={{ display: 'inline', color: "grey" }}> cm {patient.height} </h5>
</div>

<div>
  <h3 style={{ display: 'inline', color: "grey" }}> <RadiusBottomrightOutlined   rev={undefined} /> Piede: </h3>
  <h5 style={{ display: 'inline', color: "grey" }}> EU {patient.footSize} </h5>
</div>

    </Col>

    <Col span={8}>
    <div>
    <h3 style={{ display: 'inline', color: "grey" }}> 
    <HomeOutlined 
      rev={undefined} 
      onClick={() => { navigator.clipboard.writeText(`${patient.street} ${patient.streetNumber}, ${patient.cap} ${patient.city} (${patient.province})`); alert('Indirizzo Copiato!'); }} 
    /> Indirizzo: 
    </h3>
    <h5 style={{ display: 'inline', color: "grey" }}> {patient.street} {patient.streetNumber}, {patient.cap} {patient.city} &#40;{patient.province}&#41; </h5>
  </div>

  <div>
    <h3 style={{ display: 'inline', color: "grey" }}> 
    <MailOutlined 
      rev={undefined} 
      onClick={() => { window.open(`mailto:${patient.email}`, '_blank'); }} 
    /> Email: 
    </h3>
    <h5 style={{ display: 'inline', color: "grey" }}> {patient.email} </h5>
  </div>

  <div>
    <h3 style={{ display: 'inline', color: "grey" }}> 
    <PhoneOutlined 
      rev={undefined} 
      onClick={() => { window.open(`tel:${patient.telephone}`, '_blank'); }} 
    /> Telefono: 
    </h3>
    <h5 style={{ display: 'inline', color: "grey" }}> {patient.telephone} </h5>
  </div>

  <div>
    <h3 style={{ display: 'inline', color: "grey" }}> 
    <WhatsAppOutlined  
      rev={undefined} 
      onClick={() => { window.open(`https://wa.me/${patient.telephone}`, '_blank');  }} 
    /> Whatsapp: 
    </h3>
    <h5 style={{ display: 'inline', color: "grey" }}> {patient.telephone} </h5>
  </div>


    </Col>
    
    
    <Col span={8}>
    <div>
    <h3 style={{ display: 'inline', color: "grey" }}> 
    <ReconciliationOutlined  
      rev={undefined} 
      onClick={() => { navigator.clipboard.writeText(`${patient.protocolExample}`); alert('Protocollo Copiato!'); }} 
    /> Protocollo: 
    </h3>
    <h5 style={{ display: 'inline', color: "grey" }}> {patient.protocolExample} </h5>
  </div>
  </Col>

{patient.redFlags && patient.redFlags.trim() !== "" && (
  <Row className="redSelection2">
  <Col  span={4}>
    <h2 className="redstring" >RED FLAG</h2>
  </Col>
   <Col  span={20}>
   <h5>{patient.redFlags}</h5>
 </Col>
 </Row>
)}
          
          </Row>
    

          <Table
            className="exam-table"
            columns={columns}
            dataSource={posturalExaminations}
            pagination={{ pageSize: 30 }}
          />
          <Statistic
            title="Esami Eseguiti"
            className="staios"
            value={numberOfExams}
          />
          <DeleteExamModal
            patient={patient}
            posturalExamination={delPosturalExamination}
            visible={delPosturalExaminationVisible}
            setVisible={setDelPosturalExaminationVisible}
            setExamDelSuccess={setExamDelSuccess}
          />
          <Modal
            title="Stato Test Posturali"
            centered
            open={statusModalInfoVisible}
            footer={false}
            onCancel={() => setStatusModalInfoVisible(false)}
            width={480}
          >
            <div className="centered">
              <Tag color={"green"}>Test Positivo</Tag>
              <Tag color={"yellow"}>Test non eseguito</Tag>
              <Tag color={"red"}>Test Negativo</Tag>
            </div>
          </Modal>
        </div>
      )}

      {width < 640 && (
        <div>
           {compareVisible && (
            <>
              <FloatButton
                tooltip={<div>Compara Esami</div>}
                badge={{ count: selectedNumExams }}
                icon={ <MergeCellsOutlined rev={undefined} /> }
                type="default"
                style={{ right: 24, bottom: 100 }}
                onClick={() => {
                  navigate(
                    "/app" +
                      COMPARE_EXAMS_PATH +
                      "/" +
                      patient.id +
                      "/multiple_exams/" +
                      selectedExams.join("-")
                  );
                }}
              />
            </>
          )}
          {genPdfVisible && (
            <>
              <FloatButton
                tooltip={<div>Genera PDF</div>}
                badge={{ count: selectedNumExams }}
                icon={<FilePdfOutlined rev={undefined} />}
                type="default"
                style={{ right: 24 }}
                onClick={() => {
                  navigate(
                    "/app" +
                      PDF_EXAMS_PATH +
                      "/" +
                      patient.id +
                      "/multiple_exams/" +
                      selectedExams.join("-")
                  );
                }}
              />
            </>
          )}

{patient.telephone && 
  (
        <>

          {patient.email && (
          <Button
            type="link"
            href={`mailto:${patient.email}`}
            icon={<MailOutlined rev={undefined} />}
          />
          )}

        {patient.street && patient.streetNumber && patient.cap && patient.city && patient.province &&
          <Button
  type="link"
  href={`https://www.google.com/maps?q=${encodeURIComponent(patient.street || '')}+${encodeURIComponent(patient.streetNumber || '')},+${encodeURIComponent(patient.cap || '')}+${encodeURIComponent(patient.city || '')}+(${encodeURIComponent(patient.province || '')})`}
  target="_blank"
  icon={<EnvironmentOutlined rev={undefined} />} // Usa l'icona adatta, in questo caso ho usato EnvironmentOutlined
/>
}
          <Button
            type="link"
            href={`tel:${patient.telephone}`}
            icon={<PhoneOutlined rev={undefined} />}
            
          />
          {patient.telephone.startsWith("+393") && (
            <Button
              type="link"
              href={`https://wa.me/${patient.telephone}`}
              target="_blank"
              icon={<WhatsAppOutlined rev={undefined} />}
            />
          )}
        </>
  )}

        
   
   {patient.redFlags && patient.redFlags.trim() !== "" && (
  <Row className="redSelection2">
  
   <Col  span={24}>
   <h5>{patient.redFlags}</h5>
 </Col>
 </Row>
)}




          <div className="sss"></div>
          <Table
            className="exam-table"
            columns={columns2}
            dataSource={posturalExaminations}
            pagination={{ pageSize: 40 }}
          />
          <DeleteExamModal
            patient={patient}
            posturalExamination={delPosturalExamination}
            visible={delPosturalExaminationVisible}
            setVisible={setDelPosturalExaminationVisible}
            setExamDelSuccess={setExamDelSuccess}
          />
          <Modal
            title="Test posturali status bar"
            centered
            open={statusModalInfoVisible}
            footer={false}
            onCancel={() => setStatusModalInfoVisible(false)}
            width={480}
          >
            <div className="centered">
              <Tag color={"red"}>Test Negativo</Tag>
              <Tag color={"green"}>Test Positivo</Tag>
              <Tag color={"yellow"}>Test non eseguito</Tag>
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};
export default PatientPosturalExamsTable;
function getPosturalExamPDFBlob(examId: number) {
  throw new Error("Function not implemented.");
}
